/**
 * usePortfolioSnapshots Hook
 *
 * Manage portfolio snapshots for tracking historical state
 * and comparing portfolio changes over time.
 *
 * Syncs with Supabase for authenticated users, falls back to localStorage.
 */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';
import type { Token, Prices } from '../types';

export interface PortfolioSnapshot {
  id: string;
  name: string;
  createdAt: string;
  tokens: Token[];
  prices: Prices;
  totalValueUSD: number;
  totalCostBasis: number;
  totalPnL: number;
  totalPnLPercent: number;
  tokenCount: number;
  positionCount: number;
  notes?: string;
  portfolioId?: string;
}

export interface SnapshotComparison {
  snapshot1: PortfolioSnapshot;
  snapshot2: PortfolioSnapshot;
  valueDiff: number;
  valueDiffPercent: number;
  pnlDiff: number;
  tokenChanges: {
    added: string[];
    removed: string[];
    changed: string[];
  };
}

const STORAGE_KEY = 'edouard_portfolio_snapshots';
const MAX_SNAPSHOTS = 50;

/**
 * Calculate portfolio metrics from tokens and prices
 */
function calculatePortfolioMetrics(tokens: Token[], prices: Prices) {
  let totalValueUSD = 0;
  let totalCostBasis = 0;
  let positionCount = 0;

  tokens.forEach(token => {
    const price = prices[token.id]?.usd || 0;

    token.positions.forEach(pos => {
      if (pos.type === 'buy' || pos.type === 'mining' || pos.type === 'staking') {
        totalValueUSD += pos.quantity * price;
        totalCostBasis += pos.quantity * pos.price;
        positionCount++;
      }
    });
  });

  const totalPnL = totalValueUSD - totalCostBasis;
  const totalPnLPercent = totalCostBasis > 0 ? (totalPnL / totalCostBasis) * 100 : 0;

  return {
    totalValueUSD,
    totalCostBasis,
    totalPnL,
    totalPnLPercent,
    tokenCount: tokens.length,
    positionCount,
  };
}

/**
 * Generate a unique ID for snapshots
 */
function generateSnapshotId(): string {
  return `snapshot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Load snapshots from localStorage
 */
function loadSnapshotsFromLocalStorage(): PortfolioSnapshot[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Error loading snapshots from localStorage:', error);
  }
  return [];
}

/**
 * Save snapshots to localStorage
 */
function saveSnapshotsToLocalStorage(snapshots: PortfolioSnapshot[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshots));
  } catch (error) {
    console.error('Error saving snapshots to localStorage:', error);
  }
}

export function usePortfolioSnapshots(portfolioId?: string) {
  const { user, isAuthenticated } = useAuthStore();
  const [snapshots, setSnapshots] = useState<PortfolioSnapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load snapshots on mount or when auth state changes
  useEffect(() => {
    async function loadSnapshots() {
      setIsLoading(true);

      if (isAuthenticated && user?.id) {
        // Load from Supabase
        try {
          let query = supabase
            .from('portfolio_snapshots')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(MAX_SNAPSHOTS);

          if (portfolioId) {
            query = query.eq('portfolio_id', portfolioId);
          }

          const { data, error } = await query;

          if (error) {
            console.error('Error loading snapshots from Supabase:', error);
            // Fallback to localStorage
            const localSnapshots = loadSnapshotsFromLocalStorage();
            setSnapshots(localSnapshots);
          } else if (data) {
            // Transform Supabase data to local format
            const transformedSnapshots: PortfolioSnapshot[] = data.map(row => ({
              id: row.id,
              name: row.snapshot_date, // Use date as name, or we could add a name column
              createdAt: row.created_at,
              tokens: (row.holdings as any)?.tokens || [],
              prices: (row.holdings as any)?.prices || {},
              totalValueUSD: row.total_value,
              totalCostBasis: row.total_cost,
              totalPnL: row.total_pl,
              totalPnLPercent: row.total_pl_pct || 0,
              tokenCount: row.token_count,
              positionCount: row.position_count,
              notes: (row.holdings as any)?.notes,
              portfolioId: row.portfolio_id,
            }));
            setSnapshots(transformedSnapshots);
            // Also update localStorage as cache
            saveSnapshotsToLocalStorage(transformedSnapshots);
            console.log('[Snapshots] Loaded from Supabase:', transformedSnapshots.length);
          }
        } catch (err) {
          console.error('Error fetching snapshots:', err);
          const localSnapshots = loadSnapshotsFromLocalStorage();
          setSnapshots(localSnapshots);
        }
      } else {
        // Not authenticated, load from localStorage
        const localSnapshots = loadSnapshotsFromLocalStorage();
        setSnapshots(localSnapshots);
      }

      setIsLoading(false);
    }

    loadSnapshots();
  }, [isAuthenticated, user?.id, portfolioId]);

  /**
   * Create a new snapshot from current portfolio state
   */
  const createSnapshot = useCallback(async (
    tokens: Token[],
    prices: Prices,
    name?: string,
    notes?: string
  ): Promise<PortfolioSnapshot> => {
    const metrics = calculatePortfolioMetrics(tokens, prices);
    const snapshotId = generateSnapshotId();
    const now = new Date().toISOString();

    const snapshot: PortfolioSnapshot = {
      id: snapshotId,
      name: name || `Snapshot ${new Date().toLocaleDateString()}`,
      createdAt: now,
      tokens: JSON.parse(JSON.stringify(tokens)), // Deep clone
      prices: JSON.parse(JSON.stringify(prices)), // Deep clone
      ...metrics,
      notes,
      portfolioId,
    };

    // Save to Supabase if authenticated
    if (isAuthenticated && user?.id) {
      try {
        const { data, error } = await supabase
          .from('portfolio_snapshots')
          .insert({
            id: snapshotId,
            user_id: user.id,
            portfolio_id: portfolioId || 'default',
            snapshot_date: snapshot.name,
            total_value: metrics.totalValueUSD,
            total_cost: metrics.totalCostBasis,
            total_pl: metrics.totalPnL,
            total_pl_pct: metrics.totalPnLPercent,
            realized_pl: 0,
            unrealized_pl: metrics.totalPnL,
            holdings: {
              tokens: snapshot.tokens,
              prices: snapshot.prices,
              notes: notes,
            },
            calculation_method: 'FIFO',
            token_count: metrics.tokenCount,
            position_count: metrics.positionCount,
            created_at: now,
          })
          .select()
          .single();

        if (error) {
          console.error('Error saving snapshot to Supabase:', error);
        } else {
          console.log('[Snapshots] Saved to Supabase:', data?.id);
        }
      } catch (err) {
        console.error('Error creating snapshot in Supabase:', err);
      }
    }

    // Update local state and localStorage
    setSnapshots(prev => {
      const updated = [snapshot, ...prev].slice(0, MAX_SNAPSHOTS);
      saveSnapshotsToLocalStorage(updated);
      return updated;
    });

    return snapshot;
  }, [isAuthenticated, user?.id, portfolioId]);

  /**
   * Delete a snapshot by ID
   */
  const deleteSnapshot = useCallback(async (snapshotId: string): Promise<void> => {
    // Delete from Supabase if authenticated
    if (isAuthenticated && user?.id) {
      try {
        const { error } = await supabase
          .from('portfolio_snapshots')
          .delete()
          .eq('id', snapshotId)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error deleting snapshot from Supabase:', error);
        } else {
          console.log('[Snapshots] Deleted from Supabase:', snapshotId);
        }
      } catch (err) {
        console.error('Error deleting snapshot:', err);
      }
    }

    // Update local state and localStorage
    setSnapshots(prev => {
      const updated = prev.filter(s => s.id !== snapshotId);
      saveSnapshotsToLocalStorage(updated);
      return updated;
    });
  }, [isAuthenticated, user?.id]);

  /**
   * Update a snapshot's name or notes
   */
  const updateSnapshot = useCallback(async (
    snapshotId: string,
    updates: { name?: string; notes?: string }
  ): Promise<void> => {
    // Update in Supabase if authenticated
    if (isAuthenticated && user?.id) {
      try {
        // Get current snapshot to update holdings
        const snapshot = snapshots.find(s => s.id === snapshotId);
        if (snapshot) {
          const { error } = await supabase
            .from('portfolio_snapshots')
            .update({
              snapshot_date: updates.name || snapshot.name,
              holdings: {
                tokens: snapshot.tokens,
                prices: snapshot.prices,
                notes: updates.notes || snapshot.notes,
              },
            })
            .eq('id', snapshotId)
            .eq('user_id', user.id);

          if (error) {
            console.error('Error updating snapshot in Supabase:', error);
          } else {
            console.log('[Snapshots] Updated in Supabase:', snapshotId);
          }
        }
      } catch (err) {
        console.error('Error updating snapshot:', err);
      }
    }

    // Update local state and localStorage
    setSnapshots(prev => {
      const updated = prev.map(s =>
        s.id === snapshotId ? { ...s, ...updates } : s
      );
      saveSnapshotsToLocalStorage(updated);
      return updated;
    });
  }, [isAuthenticated, user?.id, snapshots]);

  /**
   * Compare two snapshots
   */
  const compareSnapshots = useCallback((
    snapshot1Id: string,
    snapshot2Id: string
  ): SnapshotComparison | null => {
    const s1 = snapshots.find(s => s.id === snapshot1Id);
    const s2 = snapshots.find(s => s.id === snapshot2Id);

    if (!s1 || !s2) return null;

    const tokens1 = new Set(s1.tokens.map(t => t.id));
    const tokens2 = new Set(s2.tokens.map(t => t.id));

    const added = [...tokens2].filter(id => !tokens1.has(id));
    const removed = [...tokens1].filter(id => !tokens2.has(id));
    const changed = [...tokens1].filter(id => {
      if (!tokens2.has(id)) return false;
      const t1 = s1.tokens.find(t => t.id === id);
      const t2 = s2.tokens.find(t => t.id === id);
      if (!t1 || !t2) return false;
      return t1.positions.length !== t2.positions.length;
    });

    return {
      snapshot1: s1,
      snapshot2: s2,
      valueDiff: s2.totalValueUSD - s1.totalValueUSD,
      valueDiffPercent: s1.totalValueUSD > 0
        ? ((s2.totalValueUSD - s1.totalValueUSD) / s1.totalValueUSD) * 100
        : 0,
      pnlDiff: s2.totalPnL - s1.totalPnL,
      tokenChanges: { added, removed, changed },
    };
  }, [snapshots]);

  /**
   * Get a specific snapshot by ID
   */
  const getSnapshot = useCallback((snapshotId: string): PortfolioSnapshot | undefined => {
    return snapshots.find(s => s.id === snapshotId);
  }, [snapshots]);

  /**
   * Export snapshots as JSON
   */
  const exportSnapshots = useCallback((): string => {
    return JSON.stringify(snapshots, null, 2);
  }, [snapshots]);

  /**
   * Import snapshots from JSON
   */
  const importSnapshots = useCallback(async (jsonData: string): Promise<boolean> => {
    try {
      const imported = JSON.parse(jsonData) as PortfolioSnapshot[];
      if (!Array.isArray(imported)) return false;

      // Validate and merge
      const validSnapshots = imported.filter(s =>
        s.id && s.name && s.createdAt && s.tokens && s.prices
      );

      // If authenticated, also save to Supabase
      if (isAuthenticated && user?.id) {
        for (const snapshot of validSnapshots) {
          try {
            const metrics = calculatePortfolioMetrics(snapshot.tokens, snapshot.prices);
            await supabase
              .from('portfolio_snapshots')
              .upsert({
                id: snapshot.id,
                user_id: user.id,
                portfolio_id: snapshot.portfolioId || portfolioId || 'default',
                snapshot_date: snapshot.name,
                total_value: metrics.totalValueUSD,
                total_cost: metrics.totalCostBasis,
                total_pl: metrics.totalPnL,
                total_pl_pct: metrics.totalPnLPercent,
                realized_pl: 0,
                unrealized_pl: metrics.totalPnL,
                holdings: {
                  tokens: snapshot.tokens,
                  prices: snapshot.prices,
                  notes: snapshot.notes,
                },
                calculation_method: 'FIFO',
                token_count: metrics.tokenCount,
                position_count: metrics.positionCount,
                created_at: snapshot.createdAt,
              }, {
                onConflict: 'id',
              });
          } catch (err) {
            console.error('Error importing snapshot to Supabase:', err);
          }
        }
      }

      setSnapshots(prev => {
        const merged = [...validSnapshots, ...prev]
          .filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i) // Dedupe
          .slice(0, MAX_SNAPSHOTS);
        saveSnapshotsToLocalStorage(merged);
        return merged;
      });

      return true;
    } catch (error) {
      console.error('Error importing snapshots:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, portfolioId]);

  /**
   * Clear all snapshots
   */
  const clearAllSnapshots = useCallback(async (): Promise<void> => {
    // Delete all from Supabase if authenticated
    if (isAuthenticated && user?.id) {
      try {
        let query = supabase
          .from('portfolio_snapshots')
          .delete()
          .eq('user_id', user.id);

        if (portfolioId) {
          query = query.eq('portfolio_id', portfolioId);
        }

        const { error } = await query;
        if (error) {
          console.error('Error clearing snapshots from Supabase:', error);
        } else {
          console.log('[Snapshots] Cleared all from Supabase');
        }
      } catch (err) {
        console.error('Error clearing snapshots:', err);
      }
    }

    setSnapshots([]);
    saveSnapshotsToLocalStorage([]);
  }, [isAuthenticated, user?.id, portfolioId]);

  return {
    snapshots,
    isLoading,
    createSnapshot,
    deleteSnapshot,
    updateSnapshot,
    compareSnapshots,
    getSnapshot,
    exportSnapshots,
    importSnapshots,
    clearAllSnapshots,
    snapshotCount: snapshots.length,
    maxSnapshots: MAX_SNAPSHOTS,
  };
}
